import jsPDF from 'jspdf';
import { Exercise } from '../types/exercise';

export const pdfService = {
  async generateExercisePDF(exercise: Exercise): Promise<void> {
    const pdf = new jsPDF();
    
    // Set up fonts and colors
    const primaryColor = '#1e40af'; // Blue color for branding
    const textColor = '#374151'; // Dark gray for text
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    
    // Header with branding
    pdf.setFontSize(24);
    pdf.setTextColor(primaryColor);
    pdf.text('Crisis Compass', margin, 30);
    
    pdf.setFontSize(16);
    pdf.setTextColor(textColor);
    pdf.text('Exercise Documentation', margin, 45);
    
    // Exercise title
    pdf.setFontSize(20);
    pdf.setTextColor(primaryColor);
    pdf.text(exercise.title, margin, 65);
    
    // Basic information
    let yPosition = 85;
    pdf.setFontSize(12);
    pdf.setTextColor(textColor);
    
    const basicInfo = [
      `Scenario Type: ${exercise.scenarioType}`,
      `Date: ${exercise.exerciseDate} at ${exercise.exerciseTime}`,
      `Location: ${exercise.location}`,
      `Status: ${exercise.status}`,
      `Created by: ${exercise.createdByEmail}`
    ];
    
    basicInfo.forEach((info) => {
      pdf.text(info, margin, yPosition);
      yPosition += 8;
    });
    
    // Description section
    yPosition += 10;
    pdf.setFontSize(14);
    pdf.setTextColor(primaryColor);
    pdf.text('Exercise Description', margin, yPosition);
    yPosition += 10;
    
    pdf.setFontSize(10);
    pdf.setTextColor(textColor);
    if (exercise.description) {
      const descriptionLines = pdf.splitTextToSize(exercise.description, contentWidth);
      pdf.text(descriptionLines, margin, yPosition);
      yPosition += descriptionLines.length * 6;
    }
    
    // Learning objectives
    yPosition += 10;
    pdf.setFontSize(14);
    pdf.setTextColor(primaryColor);
    pdf.text('Learning Objectives', margin, yPosition);
    yPosition += 10;
    
    pdf.setFontSize(10);
    pdf.setTextColor(textColor);
    exercise.objectives.forEach((objective, index) => {
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 30;
      }
      pdf.text(`${index + 1}. ${objective}`, margin, yPosition);
      yPosition += 8;
    });
    
    // Exercise injects
    if (exercise.injects.length > 0) {
      yPosition += 10;
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = 30;
      }
      
      pdf.setFontSize(14);
      pdf.setTextColor(primaryColor);
      pdf.text('Exercise Injects', margin, yPosition);
      yPosition += 10;
      
      // Table headers
      pdf.setFontSize(10);
      pdf.setTextColor(textColor);
      pdf.text('Time (min)', margin, yPosition);
      pdf.text('Inject Content', margin + 30, yPosition);
      yPosition += 8;
      
      // Draw line under headers
      pdf.line(margin, yPosition - 2, pageWidth - margin, yPosition - 2);
      yPosition += 5;
      
      exercise.injects.forEach((inject) => {
        if (yPosition > pageHeight - 40) {
          pdf.addPage();
          yPosition = 30;
        }
        
        pdf.text(inject.timeOffsetMinutes.toString(), margin, yPosition);
        const contentLines = pdf.splitTextToSize(inject.content, contentWidth - 40);
        pdf.text(contentLines, margin + 30, yPosition);
        yPosition += Math.max(8, contentLines.length * 6);
      });
    }
    
    // Regulatory triggers
    if (exercise.regulatoryTriggers.length > 0) {
      yPosition += 15;
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 30;
      }
      
      pdf.setFontSize(14);
      pdf.setTextColor(primaryColor);
      pdf.text('Regulatory Compliance', margin, yPosition);
      yPosition += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(textColor);
      const triggersText = exercise.regulatoryTriggers.join(', ');
      const triggersLines = pdf.splitTextToSize(`Frameworks: ${triggersText}`, contentWidth);
      pdf.text(triggersLines, margin, yPosition);
      yPosition += triggersLines.length * 6;
    }
    
    // Notes section
    if (exercise.notes) {
      yPosition += 15;
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 30;
      }
      
      pdf.setFontSize(14);
      pdf.setTextColor(primaryColor);
      pdf.text('Additional Notes', margin, yPosition);
      yPosition += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(textColor);
      const notesLines = pdf.splitTextToSize(exercise.notes, contentWidth);
      pdf.text(notesLines, margin, yPosition);
    }
    
    // Footer on each page
    const totalPages = pdf.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor('#9CA3AF');
      pdf.text(
        `Generated by Crisis Compass Exercise Creator - ${new Date().toLocaleDateString()}`,
        margin,
        pageHeight - 15
      );
      pdf.text(
        `Page ${i} of ${totalPages}`,
        pageWidth - margin - 20,
        pageHeight - 15
      );
    }
    
    // Save the PDF
    const fileName = `${exercise.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_exercise.pdf`;
    pdf.save(fileName);
  }
};

